// Website pixels: the New-pixel modal. A client-side mirror of the required
// subset of `Pixel::validate`, prefilled from the organization defaults.

import { useEffect, useState } from 'react';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { Icons } from '@/components/primitives';
import type { PixelDefaults } from '@/api/types';
import { useMe } from '@/auth/useMe';
import { MutationErrorLine } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { toWireDomain } from '@/lib/pixel-draft';
import type { NewPixelBody } from '@/lib/pixel-draft';

export interface NewPixelModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (body: NewPixelBody) => void;
  submitting: boolean;
  error: Error | null;
  onResetError?: () => void;
  defaults: PixelDefaults | undefined;
}

export function NewPixelModal({ open, onClose, onCreate, submitting, error, onResetError, defaults }: NewPixelModalProps) {
  const { data: me } = useMe();
  const myEmail = me?.email ?? '';
  const [id, setId] = useState('');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState(myEmail);
  const [tenant, setTenant] = useState(defaults?.tenant_id ?? '');
  const [domain, setDomain] = useState('');
  const [metaPixelId, setMetaPixelId] = useState(defaults?.meta_pixel_id ?? '');
  const [ttdAdvId, setTtdAdvId] = useState(defaults?.ttd_adv_id ?? '');
  const [ttdCtId, setTtdCtId] = useState(defaults?.ttd_ct_id ?? '');
  const [ttdPageviewId, setTtdPageviewId] = useState(defaults?.ttd_ct_id_pageview ?? '');
  const [attempted, setAttempted] = useState(false);
  const required = useRequiredFeedback();
  useEffect(() => {
    if (open) {
      // Default the owner to the creator's own email; they can override it.
      setId(''); setDescription(''); setOwner(myEmail); setTenant(defaults?.tenant_id ?? ''); setDomain('');
      setMetaPixelId(defaults?.meta_pixel_id ?? '');
      setTtdAdvId(defaults?.ttd_adv_id ?? '');
      setTtdCtId(defaults?.ttd_ct_id ?? '');
      setTtdPageviewId(defaults?.ttd_ct_id_pageview ?? '');
      setAttempted(false);
      required.clearAll();
      onResetError?.();
    }
  }, [open]);
  // If the session resolves after the modal is already open, fill the still-empty
  // Owner without clobbering anything the user has started typing.
  useEffect(() => {
    if (open && myEmail && !owner) {
      setOwner(myEmail);
      required.clear('new-pixel-owner');
    }
  }, [open, myEmail]);
  // Same shape as the Owner back-fill above: `tenant` is deliberately NOT a
  // dependency. Including it makes the effect re-run the moment the field is
  // emptied, which silently re-types the default and leaves the tenant field
  // impossible to clear.
  useEffect(() => {
    if (open && defaults?.tenant_id && !tenant) {
      setTenant(defaults.tenant_id);
      required.clear('new-pixel-tenant');
    }
  }, [open, defaults?.tenant_id]);
  if (!open) return null;
  const slug = id.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  const wireDomain = toWireDomain(domain);
  // Client-side mirror of the REQUIRED subset of Pixel::validate(), so the user
  // learns exactly what's missing before submitting. Description is deliberately
  // absent — the server doesn't require it. The server still re-validates and
  // surfaces its own email/provider/domain-shape failures through `error` below.
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(owner.trim());
  const metaId = metaPixelId.trim();
  const ttdAdv = ttdAdvId.trim();
  const ttdCt = ttdCtId.trim();
  const ttdPv = ttdPageviewId.trim();
  const metaValid = !metaId || /^\d{10,20}$/.test(metaId);
  const ttdPresent = !!(ttdAdv || ttdCt || ttdPv);
  const ttdValid = !ttdPresent || (!!ttdAdv && !!(ttdCt || ttdPv)
    && (!ttdCt || ttdCt.startsWith('0:'))
    && (!ttdPv || ttdPv.startsWith('0:')));
  const providerError = !metaId && !ttdPresent
    ? 'Configure at least one provider: Meta or The Trade Desk.'
    : !metaValid
      ? 'Meta pixel ID must contain 10–20 digits.'
      : !ttdValid
        ? 'TTD requires an advertiser ID and at least one 0:-prefixed tracking-tag ID.'
        : null;
  const fieldErr = {
    id:     slug.length < 3 ? 'Enter an ID — at least 3 characters (lowercase + underscores)' : null,
    owner:  !emailOk        ? 'Enter a valid owner email' : null,
    tenant: !tenant.trim()  ? 'Enter a tenant' : null,
    domain: !wireDomain     ? 'Enter the install domain' : null,
    provider: providerError,
  };
  const problems = Object.values(fieldErr).filter(Boolean);
  const submit = () => {
    if (submitting) return;
    // Never a silent no-op: an incomplete form reveals per-field reasons.
    const valid = required.report([
      fieldErr.id ? { key: 'new-pixel-id', message: fieldErr.id } : null,
      fieldErr.owner ? { key: 'new-pixel-owner', message: fieldErr.owner } : null,
      fieldErr.tenant ? { key: 'new-pixel-tenant', message: fieldErr.tenant } : null,
      fieldErr.domain ? { key: 'new-pixel-domain', message: fieldErr.domain } : null,
      fieldErr.provider ? { key: 'new-pixel-provider', message: fieldErr.provider } : null,
    ]);
    if (!valid) {
      setAttempted(true);
      return;
    }
    setAttempted(false);
    onCreate({
      id: slug,
      tenant_id: tenant,
      description,
      owner_email: owner.trim(),
      domains: [wireDomain],
      meta_pixel_id: metaId || null,
      ttd_adv_id: ttdAdv || null,
      ttd_ct_id: ttdCt || null,
      ttd_ct_id_pageview: ttdPv || null,
      default_venue_id: null,
      default_production_id: null,
    });
  };
  return (
    <ResponsiveOverlay label="pixels" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={e=>e.stopPropagation()} style={{maxWidth:520}}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">New pixel</div>
            <div className="muted" style={{fontSize:11.5,marginTop:2}}>{defaults?.configured ? 'Organization defaults are prefilled below; review them before creating.' : 'No organization provider defaults are configured. Enter at least one provider below.'}</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <RequiredField
            fieldKey="new-pixel-id"
            message={required.messages['new-pixel-id']}
            attempt={required.attempt}
            className="field"
            style={{marginBottom:12}}
          >
            <label className="field-lbl"><span>ID <span style={{color:'var(--bad)'}}>*</span></span> <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>TOML key · lowercase + underscores</span></label>
            <input
              className="input mono"
              style={{fontSize:12}}
              placeholder="e.g. summer26"
              value={id}
              onChange={e => {
                const next = e.target.value;
                setId(next);
                const nextSlug = next.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                if (nextSlug.length >= 3) required.clear('new-pixel-id');
              }}
              autoFocus
            />
            {id && slug !== id && <div className="field-hint">Will be saved as <code style={{fontFamily:'var(--font-mono)'}}>{slug}</code></div>}
          </RequiredField>
          <div className="field" style={{marginBottom:12}}>
            <label className="field-lbl"><span>Description</span> <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>optional</span></label>
            <input className="input" placeholder="One-line summary of where this pixel fires" value={description} onChange={e=>setDescription(e.target.value)}/>
          </div>
          <div className="stack-phone" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
            <RequiredField
              fieldKey="new-pixel-owner"
              message={required.messages['new-pixel-owner']}
              attempt={required.attempt}
              className="field"
              style={{marginBottom:0}}
            >
              <label className="field-lbl"><span>Owner <span style={{color:'var(--bad)'}}>*</span></span></label>
              <input
                className="input mono"
                style={{fontSize:12}}
                placeholder="email@shubert.com"
                value={owner}
                onChange={e => {
                  const next = e.target.value;
                  setOwner(next);
                  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(next.trim())) {
                    required.clear('new-pixel-owner');
                  }
                }}
              />
            </RequiredField>
            <RequiredField
              fieldKey="new-pixel-tenant"
              message={required.messages['new-pixel-tenant']}
              attempt={required.attempt}
              className="field"
              style={{marginBottom:0}}
            >
              <label className="field-lbl"><span>Tenant <span style={{color:'var(--bad)'}}>*</span></span></label>
              <input
                className="input mono"
                style={{fontSize:12}}
                placeholder="tenant slug"
                value={tenant}
                onChange={e => {
                  const next = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g,'_');
                  setTenant(next);
                  if (next.trim()) required.clear('new-pixel-tenant');
                }}
              />
            </RequiredField>
          </div>
          <RequiredField
            fieldKey="new-pixel-domain"
            message={required.messages['new-pixel-domain']}
            attempt={required.attempt}
            className="field"
          >
            <label className="field-lbl"><span>Domain <span style={{color:'var(--bad)'}}>*</span></span> <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>where the snippet will be installed</span></label>
            <input
              className="input mono"
              style={{fontSize:12}}
              placeholder="test.com or https://us.test.com"
              value={domain}
              onChange={e => {
                const next = e.target.value;
                setDomain(next);
                if (toWireDomain(next)) required.clear('new-pixel-domain');
              }}
            />
            {domain.trim() && wireDomain && wireDomain !== domain.trim() && (
              <div className="field-hint">Will be saved as <code style={{fontFamily:'var(--font-mono)'}}>{wireDomain}</code> (covers all subdomains)</div>
            )}
          </RequiredField>
          <RequiredField
            fieldKey="new-pixel-provider"
            message={required.messages['new-pixel-provider']}
            attempt={required.attempt}
            className="field"
            style={{marginTop:12}}
          >
            <label className="field-lbl">
              <span>Provider IDs <span style={{color:'var(--bad)'}}>*</span></span>
              <span className="muted" style={{textTransform:'none',letterSpacing:0,marginLeft:6}}>at least Meta or TTD</span>
            </label>
            <div className="panel stack-phone" style={{padding:10,display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <input className="input mono" style={{fontSize:11}} placeholder="Meta pixel ID" value={metaPixelId}
                onChange={e => { setMetaPixelId(e.target.value); required.clear('new-pixel-provider'); }}/>
              <input className="input mono" style={{fontSize:11}} placeholder="TTD advertiser ID" value={ttdAdvId}
                onChange={e => { setTtdAdvId(e.target.value); required.clear('new-pixel-provider'); }}/>
              <input className="input mono" style={{fontSize:11}} placeholder="TTD conversion tag · 0:…" value={ttdCtId}
                onChange={e => { setTtdCtId(e.target.value); required.clear('new-pixel-provider'); }}/>
              <input className="input mono" style={{fontSize:11}} placeholder="TTD pageview tag · 0:…" value={ttdPageviewId}
                onChange={e => { setTtdPageviewId(e.target.value); required.clear('new-pixel-provider'); }}/>
            </div>
            <div className="field-hint">Provider identifiers come from the organization defaults API when configured; secrets are never exposed here.</div>
          </RequiredField>
          {attempted && problems.length > 0 && (
            <div className="mono" style={{marginTop:12,fontSize:11.5,color:'var(--bad)',lineHeight:1.4}}>
              ● Fix {problems.length} field{problems.length > 1 ? 's' : ''} before creating
            </div>
          )}
          {error && <div style={{marginTop:12}}><MutationErrorLine error={error}/></div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={submitting} onClick={submit}>
            {Icons.plus} {submitting ? 'Creating…' : 'Create pixel'}
          </button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
